import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

import styles from './Field.module.css';
import { ErrorIcon } from './icons';

interface FieldFrameProps {
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  /** Presence of a message is what marks the field invalid. */
  readonly error?: ReactNode;
  readonly required?: boolean;
  /**
   * Marks the field as explicitly optional. Design addon §8.5: a form should
   * signal one or the other consistently, not leave the reader guessing.
   */
  readonly optional?: boolean;
}

type ControlIds = {
  readonly controlId: string;
  readonly describedBy: string | undefined;
  readonly invalid: boolean;
};

/**
 * Wires label, hint, and error to the control via real ids.
 *
 * `aria-describedby` is what makes a hint and an error message reach a screen
 * reader; without it they are decorative text a sighted user sees and nobody
 * else does (§8.5, §13.4).
 */
function useFieldWiring(hint: ReactNode, error: ReactNode): ControlIds {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return { controlId: `${base}-control`, describedBy, invalid: Boolean(error) };
}

function FieldFrame({
  label,
  hint,
  error,
  required,
  optional,
  controlId,
  describedBy,
  children,
}: FieldFrameProps & {
  readonly controlId: string;
  readonly describedBy: string | undefined;
  readonly children: ReactNode;
}) {
  const hintId = describedBy?.split(' ').find((id) => id.endsWith('-hint'));
  const errorId = describedBy?.split(' ').find((id) => id.endsWith('-error'));

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={controlId}>
        {label}
        {required ? (
          <span className={styles.requiredMark}>
            <span aria-hidden="true">*</span>
            <span className="visually-hidden"> (مطلوب)</span>
          </span>
        ) : null}
        {optional && !required ? <span className={styles.optionalMark}>(اختياري)</span> : null}
      </label>

      {children}

      {/* Error before hint in the DOM so it is announced first. */}
      {error ? (
        <p className={styles.error} id={errorId}>
          <span className={styles.errorIcon}>
            <ErrorIcon />
          </span>
          <span>{error}</span>
        </p>
      ) : null}
      {hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps
  extends FieldFrameProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id' | 'required'> {
  /** Tabular, LTR digits — for IDs, amounts, and phone numbers inside RTL text. */
  readonly numeric?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  required = false,
  optional = false,
  numeric = false,
  ...rest
}: TextFieldProps) {
  const { controlId, describedBy, invalid } = useFieldWiring(hint, error);

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      optional={optional}
      controlId={controlId}
      describedBy={describedBy}
    >
      <input
        {...rest}
        id={controlId}
        className={[styles.control, invalid ? styles.invalid : undefined, numeric ? styles.numeric : undefined]
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
    </FieldFrame>
  );
}

export interface TextAreaFieldProps
  extends FieldFrameProps,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id' | 'required'> {}

export function TextAreaField({
  label,
  hint,
  error,
  required = false,
  optional = false,
  ...rest
}: TextAreaFieldProps) {
  const { controlId, describedBy, invalid } = useFieldWiring(hint, error);

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      optional={optional}
      controlId={controlId}
      describedBy={describedBy}
    >
      <textarea
        {...rest}
        id={controlId}
        className={[styles.control, styles.textarea, invalid ? styles.invalid : undefined]
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
    </FieldFrame>
  );
}

export interface SelectFieldProps
  extends FieldFrameProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'id' | 'required'> {
  readonly children: ReactNode;
}

export function SelectField({
  label,
  hint,
  error,
  required = false,
  optional = false,
  children,
  ...rest
}: SelectFieldProps) {
  const { controlId, describedBy, invalid } = useFieldWiring(hint, error);

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      optional={optional}
      controlId={controlId}
      describedBy={describedBy}
    >
      <select
        {...rest}
        id={controlId}
        className={[styles.control, styles.select, invalid ? styles.invalid : undefined]
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      >
        {children}
      </select>
    </FieldFrame>
  );
}
